// src/pages/markets/board/board/columnConfig.ts
//
// Only what the backend does not already say. Filter and sort defaults come
// from schema.column_defaults — duplicating them here is how they drift.

import type { ColumnId } from '../api/boardTypes';

export type BodyMode = 'view' | 'trend';

export type CardView = 'row' | 'card';

export type CardVariant = 'score' | 'capital' | 'skill' | 'meta' | 'activity';

export interface ColumnConfig {
    // The pane's slot key: the preferences map key and the React key. Not the backend column.
    id: ColumnId;
    // The backend column this pane queries.
    source: ColumnId;
    // The trader group this pane scores, null for new/active.
    group: string | null;
    // Which menu section this category belongs under.
    section: 'conviction' | 'discovery';
    title: string;
    subtitle: string;
    // false while the backend answers 501 for this column
    available: boolean;
    defaultBody: BodyMode;
    defaultView: CardView;
    bodyVariant: CardVariant;
    showGroupToggle: boolean;
    seriesWindow: string;
    // Score-block rows, in order. Keys resolve through METRIC_REGISTRY.
    cardMetrics: string[];
    emptyText: string;
}

// A group's colour, label and seed column live in its `board_groups` document
// now — see state/boardGroups.ts. They were three two-entry maps here, which
// meant 16 of the 18 groups had no colour and rendered their internal key as a
// name.

// Stable per-area color so the reader sorts by domain before reading a word.
export const CATEGORY_COLORS: Record<string, string> = {
    politics: '#a78bfa',
    elections: '#c084fc',
    geopolitics: '#818cf8',
    finance: '#34d399',
    economics: '#2dd4bf',
    crypto: '#fbbf24',
    sports: '#60a5fa',
    tech: '#22d3ee',
    science: '#5eead4',
    culture: '#f472b6',
    entertainment: '#fb7185',
    weather: '#38bdf8',
    world: '#94a3b8',
};

const TAG_FALLBACK = ['#94a3b8', '#a3a3a3', '#a8a29e', '#9ca3af'];

export function tagColor(name: string): string {
    const key = name.trim().toLowerCase();
    const hit = CATEGORY_COLORS[key];
    if (hit) return hit;
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
    return TAG_FALLBACK[h % TAG_FALLBACK.length];
}

// GROUP_LABELS and DEFAULT_SCORE_GROUPS were here. Labels come from
// `board_groups`; the two score groups are `scoreGroups(schema)`, which reads
// them off the conviction columns' own defaults instead of naming them.

export const DEFAULT_MAX_AGE_DAYS = 7;

export const PAGE_SIZE = 30;

const CONVICTION_CARD_METRICS = [
    'dominant_capital',
    'imbalance',
    'trader_count',
    'implied_disparity',
];

const ACTIVITY_CARD_METRICS = [
    'prob_jump',
    'prob_drift',
    'volume_change',
];

// What each pane STARTS as. A pane's actual category is chosen at runtime from
// categories.ts and stored per slot; these are the seed and the fallback.
export const BOARD_COLUMNS: ColumnConfig[] = [
    {
        id: 'insider',
        source: 'insider',
        // Null, not a group name: the pane's starting group is whatever the
        // backend declares as this column's default. seedCategory() fills it in
        // from the schema.
        group: null,
        section: 'conviction',
        title: 'Informed',
        subtitle: 'Largest informed positioning',
        available: true,
        defaultBody: 'view',
        defaultView: 'row',
        bodyVariant: 'capital',
        showGroupToggle: true,
        seriesWindow: '7d',
        cardMetrics: CONVICTION_CARD_METRICS,
        emptyText: 'No informed conviction above the current thresholds.',
    },
    {
        id: 'all_traders',
        source: 'all_traders',
        // See above: filled from the schema, not named here.
        group: null,
        section: 'conviction',
        title: 'Whales',
        subtitle: 'Largest whale positioning',
        available: true,
        defaultBody: 'view',
        defaultView: 'row',
        bodyVariant: 'skill',
        showGroupToggle: true,
        seriesWindow: '7d',
        cardMetrics: CONVICTION_CARD_METRICS,
        emptyText: 'No whale conviction above the current thresholds.',
    },
    {
        id: 'new',
        source: 'new',
        group: null,
        section: 'discovery',
        title: 'New',
        subtitle: 'Recently created events',
        available: true,
        defaultBody: 'view',
        defaultView: 'row',
        bodyVariant: 'meta',
        showGroupToggle: false,
        seriesWindow: '7d',
        cardMetrics: CONVICTION_CARD_METRICS,
        emptyText: 'No new events in this category.',
    },
    {
        id: 'active',
        source: 'active',
        group: null,
        section: 'discovery',
        title: 'Active',
        subtitle: 'Price moves and volume',
        available: true,
        defaultBody: 'trend',
        defaultView: 'card',
        bodyVariant: 'activity',
        showGroupToggle: false,
        seriesWindow: '24h',
        cardMetrics: ACTIVITY_CARD_METRICS,
        emptyText: 'Nothing moved above the current thresholds.',
    },
];

export const COLUMN_BY_ID: Record<string, ColumnConfig> = Object.fromEntries(
    BOARD_COLUMNS.map(c => [c.id, c]),
);

// groupColor() and groupLabel() were here. Both are now reads off the resolved
// group — `useBoardGroups().resolve(group).color` and `.titleOf(group)` — so a
// group's name and colour come from one place instead of a map per surface.

export function isHighlightTag(name: string): boolean {
    return Object.prototype.hasOwnProperty.call(CATEGORY_COLORS, name.trim().toLowerCase());
}

export interface OrderedTag {
    text: string;
    strong: boolean;
}

// Highlighted first (an explicit palette entry, or a tag the column is actively
// filtered on), grey after; each side keeps its incoming order, so the split is
// the only thing that moves.
export function orderTags(
    tags: string[],
    activeTags?: readonly string[] | null,
): OrderedTag[] {
    const active = new Set((activeTags ?? []).map(t => t.trim().toLowerCase()));
    return tags
        .map(text => ({
            text,
            strong: active.has(text.trim().toLowerCase()) || isHighlightTag(text),
        }))
        .sort((a, b) => Number(b.strong) - Number(a.strong));
}